import { useEffect, useState } from "react";
import { ActivityIndicator } from "react-native";
import { Redirect, useRouter } from "expo-router";

import { AuthScreen } from "~/components/composed/AuthScreen";
import {
  AppText,
  Button,
  ButtonText,
  CenteredScreen,
  SegmentedControl,
  Stack,
  TextField,
} from "~/components/ui";
import { useAuth } from "~/hooks/useAuth";
import { usePrimaryWorkspace, useWorkspaceSettings } from "~/hooks/workspace";
import {
  DASHBOARD_PATH,
  hasPaidAccess,
  isInitialOnboardingComplete,
  SUBSCRIBE_PATH,
  withOnboardingComplete,
} from "~/utils/onboarding";

export default function OnboardingScreen() {
  const router = useRouter();
  const {
    session,
    user,
    loading,
    updatePreferences,
    updatePreferencesPending,
    refresh,
  } = useAuth();
  const workspace = usePrimaryWorkspace();
  const settings = useWorkspaceSettings(workspace.data?.id);

  const [legalName, setLegalName] = useState("");
  const [gstRegistered, setGstRegistered] = useState(false);
  const [gstNumber, setGstNumber] = useState("");
  const [irdNumber, setIrdNumber] = useState("");
  const [nzbn, setNzbn] = useState("");
  const [invoicePrefix, setInvoicePrefix] = useState("MS");
  const [paymentInstructions, setPaymentInstructions] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (!workspace.data || hydrated) return;
    setLegalName(workspace.data.legal_name ?? workspace.data.name ?? "");
    setGstRegistered(Boolean(workspace.data.gst_registered));
    setGstNumber(workspace.data.gst_number ?? "");
    setIrdNumber(workspace.data.ird_number ?? "");
    setNzbn(workspace.data.nzbn ?? "");
    setInvoicePrefix(workspace.data.invoice_prefix || "MS");
    setPaymentInstructions(workspace.data.default_payment_instructions ?? "");
    setHydrated(true);
  }, [hydrated, workspace.data]);

  if (loading || workspace.isLoading) {
    return (
      <CenteredScreen>
        <ActivityIndicator />
      </CenteredScreen>
    );
  }

  if (!session?.user) {
    return <Redirect href="/login" />;
  }

  if (!hasPaidAccess(user)) {
    return <Redirect href={SUBSCRIBE_PATH as never} />;
  }

  if (isInitialOnboardingComplete(user)) {
    return <Redirect href={DASHBOARD_PATH as never} />;
  }

  const finish = async (saveWorkspace: boolean) => {
    setError(null);
    try {
      if (saveWorkspace && workspace.data?.id) {
        await settings.mutateAsync({
          name: workspace.data.name,
          legalName,
          gstRegistered,
          gstNumber,
          irdNumber,
          nzbn,
          invoicePrefix,
          defaultPaymentInstructions: paymentInstructions,
          address: workspace.data.address ?? "",
          contactEmail: workspace.data.contact_email ?? "",
          phone: workspace.data.phone ?? "",
          bankAccountName: workspace.data.bank_account_name ?? "",
          bankAccountNumber: workspace.data.bank_account_number ?? "",
          bankName: workspace.data.bank_name ?? "",
          defaultHourlyRate: String(
            (workspace.data.default_hourly_rate_cents ?? 0) / 100,
          ),
          taxSetAsidePercentage: String(
            workspace.data.tax_set_aside_percentage ?? 0,
          ),
          defaultInvoiceRounding:
            workspace.data.default_invoice_rounding ?? "none",
        });
      }
      await updatePreferences(withOnboardingComplete(user?.preferences));
      await refresh();
      router.replace(DASHBOARD_PATH as never);
    } catch (finishError) {
      setError(
        finishError instanceof Error
          ? finishError.message
          : "Could not save onboarding.",
      );
    }
  };

  const busy = settings.isPending || updatePreferencesPending;

  return (
    <AuthScreen
      title="Set up your business"
      subtitle="Optional details used on invoices. You can change these anytime in Settings."
    >
      <Stack gap="md">
        {error ? (
          <AppText tone="danger" size="xs">
            {error}
          </AppText>
        ) : null}

        <Stack gap="xs">
          <AppText size="xs" weight="medium">
            Legal / trading name
          </AppText>
          <TextField
            value={legalName}
            onChangeText={setLegalName}
            placeholder="As shown on invoices"
          />
        </Stack>

        <Stack gap="xs">
          <AppText size="xs" weight="medium">
            GST registered?
          </AppText>
          <SegmentedControl
            value={gstRegistered ? "yes" : "no"}
            options={[
              { label: "No", value: "no" },
              { label: "Yes", value: "yes" },
            ]}
            onChange={(value) => setGstRegistered(value === "yes")}
          />
        </Stack>

        {gstRegistered ? (
          <Stack gap="xs">
            <AppText size="xs" weight="medium">
              GST number
            </AppText>
            <TextField
              value={gstNumber}
              onChangeText={setGstNumber}
              placeholder="123-456-789"
              autoCapitalize="characters"
            />
          </Stack>
        ) : null}

        <Stack gap="xs">
          <AppText size="xs" weight="medium">
            IRD number
          </AppText>
          <TextField
            value={irdNumber}
            onChangeText={setIrdNumber}
            placeholder="Optional"
            keyboardType="number-pad"
          />
        </Stack>

        <Stack gap="xs">
          <AppText size="xs" weight="medium">
            NZBN
          </AppText>
          <TextField
            value={nzbn}
            onChangeText={setNzbn}
            placeholder="Optional"
            keyboardType="number-pad"
          />
        </Stack>

        <Stack gap="xs">
          <AppText size="xs" weight="medium">
            Invoice number prefix
          </AppText>
          <TextField
            value={invoicePrefix}
            onChangeText={setInvoicePrefix}
            placeholder="MS"
            autoCapitalize="characters"
          />
        </Stack>

        <Stack gap="xs">
          <AppText size="xs" weight="medium">
            Default payment instructions
          </AppText>
          <TextField
            value={paymentInstructions}
            onChangeText={setPaymentInstructions}
            placeholder="Bank account details, due terms, etc."
            multiline
          />
        </Stack>

        <Button disabled={busy} onPress={() => void finish(true)}>
          <ButtonText>{busy ? "Saving..." : "Save and continue"}</ButtonText>
        </Button>
        <Button
          variant="outline"
          disabled={busy}
          onPress={() => void finish(false)}
        >
          <ButtonText>Skip for now</ButtonText>
        </Button>
      </Stack>
    </AuthScreen>
  );
}
